/**
 * Phase 5 — Creator Architect Requests page
 * Route: /creator/architect-requests
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Plus, X, RefreshCw } from 'lucide-react';
import theme from '../theme';
import SafeLanguageWarnings from '../components/SafeLanguageWarnings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KICKBACK_TYPES = [
  'product', 'service', 'membership', 'employee_program',
  'utility_access', 'referral', 'payout_style', 'general',
];
const RISK_CATEGORIES = ['low', 'moderate', 'high', 'unknown', 'not_applicable'];
const SPECIALTIES = [
  'licensed_investment_professional', 'registered_broker', 'investment_adviser',
  'portfolio_manager', 'compliance_reviewer', 'business_strategist',
  'brand_advisor', 'offer_consultant', 'market_specialist',
  'general_reviewer', 'other',
];

const STATUS_COLOR = {
  submitted: theme.colors.status.info, admin_review: theme.colors.status.warning,
  assigned: theme.colors.status.info, accepted: theme.colors.status.success,
  declined: theme.colors.status.error, in_progress: theme.colors.status.warning,
  completed: theme.colors.status.success, canceled: theme.colors.text.muted,
  rejected: theme.colors.status.error, archived: theme.colors.text.muted,
  draft: theme.colors.text.muted,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const EMPTY = {
  portfolio_id: '', business_name: '', kickback_name: '', kickback_type: 'general',
  requested_specialty: 'general_reviewer', risk_category: 'unknown',
  allocation_amount: '', eligible_holder_count: '', payout_style_rules: '',
  utility_setup_summary: '', program_summary: '', compliance_notes: '',
};

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || theme.colors.text.muted;
  return (
    <span data-testid={`creator-req-status-${status}`} style={{ padding: '3px 10px', borderRadius: 999,
      background: `${c}1A`, color: c, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function CreatorArchitectRequestsPage() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [safeWarnings, setSafeWarnings] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creator/architect-requests`, auth);
      setRows(res.data?.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.business_name.trim() || !form.kickback_name.trim()) {
      toast.error('Business Name and Kickback Name are required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        portfolio_id: form.portfolio_id.trim() || null,
        allocation_amount: form.allocation_amount === '' ? null : Number(form.allocation_amount),
        eligible_holder_count: form.eligible_holder_count === '' ? null : Number(form.eligible_holder_count),
      };
      const res = await axios.post(`${API}/creator/architect-requests`, payload, auth);
      toast.success('Architect request submitted');
      const w = res.data?.safe_language_warnings || [];
      setSafeWarnings(w);
      if (w.length === 0) { setForm(EMPTY); setShowForm(false); }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="creator-architect-requests-page" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClipboardList size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900 }}>Architect Requests</div>
          </div>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: 4, maxWidth: 720 }}>
            Request Kickback Architect support for eligible programs that may need review, structure
            guidance, specialty input, or professional support.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="creator-req-refresh-btn" onClick={load}
            style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
              background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="creator-create-req-btn" onClick={() => setShowForm(true)}
            style={{ padding: '9px 14px', borderRadius: theme.radius.sm, border: 'none',
              background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Create Request
          </button>
        </div>
      </div>

      <div style={{ padding: 10, background: '#FFF7ED', border: '1px solid #FED7AA',
        borderRadius: theme.radius.sm, marginTop: 14, marginBottom: 14, fontSize: 12, color: '#7C2D12' }}>
        Kickback Architects may support eligible programs with review, structure guidance, specialty
        input, or professional support. Kickback Contract does not guarantee approval, availability,
        financial outcomes, investment performance, payouts, or legal compliance. Regulated activities
        may require properly licensed professionals and separate agreements.
      </div>

      {showForm && (
        <div data-testid="creator-req-form" style={{ ...cardStyle, marginBottom: 16 }}>
          <SafeLanguageWarnings warnings={safeWarnings} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>New Architect Request Packet</div>
            <button onClick={() => setShowForm(false)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Business Name *</label>
              <input data-testid="req-business-name" style={inputStyle}
                value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} />
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Kickback Name *</label>
              <input data-testid="req-kickback-name" style={inputStyle}
                value={form.kickback_name} onChange={e => setForm({ ...form, kickback_name: e.target.value })} />
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Portfolio ID (optional)</label>
              <input data-testid="req-portfolio-id" style={inputStyle}
                value={form.portfolio_id} onChange={e => setForm({ ...form, portfolio_id: e.target.value })} />
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Kickback Type</label>
              <select data-testid="req-kickback-type" style={inputStyle}
                value={form.kickback_type} onChange={e => setForm({ ...form, kickback_type: e.target.value })}>
                {KICKBACK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Requested Specialty</label>
              <select data-testid="req-specialty" style={inputStyle}
                value={form.requested_specialty} onChange={e => setForm({ ...form, requested_specialty: e.target.value })}>
                {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Risk Category</label>
              <select data-testid="req-risk" style={inputStyle}
                value={form.risk_category} onChange={e => setForm({ ...form, risk_category: e.target.value })}>
                {RISK_CATEGORIES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Allocation Amount (optional)</label>
              <input data-testid="req-allocation" type="number" style={inputStyle}
                value={form.allocation_amount}
                onChange={e => setForm({ ...form, allocation_amount: e.target.value })} />
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 700 }}>Eligible Holder Count</label>
              <input data-testid="req-holders" type="number" style={inputStyle}
                value={form.eligible_holder_count}
                onChange={e => setForm({ ...form, eligible_holder_count: e.target.value })} />
            </div>
          </div>
          {[
            ['req-payout-rules', 'Payout-Style Rules', 'payout_style_rules'],
            ['req-utility-setup', 'Utility Setup Summary', 'utility_setup_summary'],
            ['req-program-summary', 'Program Summary', 'program_summary'],
            ['req-compliance-notes', 'Compliance Notes', 'compliance_notes'],
          ].map(([tid, label, field]) => (
            <div key={field} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>{label}</label>
              <textarea data-testid={tid} style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })} />
            </div>
          ))}
          <button data-testid="req-submit-btn" disabled={saving} onClick={submit}
            style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: 'none',
              background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
            Submit Request
          </button>
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Your Requests</div>
          <div data-testid="req-count" style={{ fontSize: 12, color: theme.colors.text.muted }}>
            {rows.length} total
          </div>
        </div>
        {loading ? (
          <div data-testid="creator-req-loading" style={{ padding: 18 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="creator-req-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No Architect requests yet. Click <b>Create Request</b> to submit your first packet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {rows.map(r => (
              <div key={r.id} data-testid={`creator-req-row-${r.id}`}
                style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800 }}>{r.kickback_name}</div>
                  <StatusPill status={r.status} />
                  <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
                    {r.kickback_type} · {r.requested_specialty} · risk: {r.risk_category}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                  Business: <b>{r.business_name}</b>
                  {r.assigned_architect_id && <> · Assigned to Architect ID <code>{r.assigned_architect_id.slice(0, 8)}…</code></>}
                </div>
                {r.program_summary && (
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                    {r.program_summary}
                  </div>
                )}
                {r.architect_notes && (
                  <div style={{ marginTop: 6, padding: 8, background: theme.colors.bg.card,
                    border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: 12 }}>
                    <b>Architect notes:</b> {r.architect_notes}
                  </div>
                )}
                {r.decline_reason && (
                  <div style={{ marginTop: 6, padding: 8, background: '#FEF2F2',
                    border: '1px solid #FECACA', borderRadius: theme.radius.sm, fontSize: 12, color: '#991B1B' }}>
                    <b>Declined:</b> {r.decline_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
